// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../supabaseClient';
import { validateUserTenant } from '../services/tenantService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantError, setTenantError] = useState(null);

  useEffect(() => {
    // Check active sessions - skip tenant validation here since Supabase client
    // may not be fully ready. Validation happens in onAuthStateChange instead.
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes on auth state (sign in, sign out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (_event === 'SIGNED_IN' && session?.user) {
          try {
            const validation = await validateUserTenant(session.user);
            if (validation.isAzureUser && !validation.valid) {
              console.warn('Unauthorized tenant attempted sign in:', validation.tenantId);
              await supabase.auth.signOut();
              setTenantError('Your organization is not authorized to access this application.');
              setUser(null);
              setLoading(false);
              return;
            }
            setTenantError(null);
            setUser(session.user);
          } catch (err) {
            console.error('Tenant validation error:', err);
            // On error, allow user through (fail-open) - table may not exist yet
            setUser(session.user);
            setTenantError(null);
          }
        } else if (_event === 'SIGNED_OUT') {
          setUser(null);
        } else {
          setUser(session?.user ?? null);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Sign in with Azure AD (Microsoft)
  const signInWithAzure = async () => {
    setTenantError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: 'email',
        redirectTo: `${window.location.origin}/`
      }
    });

    if (error) {
      console.error('Error signing in with Azure:', error.message);
      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    setTenantError(null);
    try {
      const { error } = await supabase.auth.signOut();
      if (error && error.message !== 'Auth session missing!') {
        console.error('Error signing out:', error.message);
        throw error;
      }
    } catch (err) {
      // Ignore "session missing" errors - user is already signed out
      if (!err.message?.includes('session missing')) {
        throw err;
      }
    }
    // Always clear user state regardless of errors
    setUser(null);
  };

  // Clear tenant error (for UI to call after displaying)
  const clearTenantError = () => {
    setTenantError(null);
  };

  // For future external user authentication
  // This structure makes it easy to add email/password or magic link auth later
  const signInWithEmail = async (email, password) => {
    // To be implemented when external users need access
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      console.error('Error signing in with email:', error.message);
      throw error;
    }

    return data;
  };

  const signUpWithEmail = async (email, password, metadata = {}) => {
    // To be implemented when external users need access
    // Can include approval workflow in metadata
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          ...metadata,
          requires_approval: true, // Flag for admin approval
        }
      }
    });

    if (error) {
      console.error('Error signing up with email:', error.message);
      throw error;
    }

    return data;
  };

  const value = {
    user,
    loading,
    tenantError,
    clearTenantError,
    signInWithAzure,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    // Helper to check if user is authenticated
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
